/**
 * Schreibt eine eingelesene Altplan-Version (altplanAnlage.ts) nach Supabase.
 *
 * Reihenfolge und Rücknahme wie bei „Neue Version erstellen“
 * (SiGePlanEditor.handleCreateVersion): erst der Kopfsatz, dann die Kinder in
 * Fremdschlüssel-Reihenfolge; scheitert ein Schritt, werden die schon
 * geschriebenen Zeilen rückwärts wieder gelöscht — kein halber Plan.
 */

import { supabase } from '../../services/supabaseClient';
import { beanstandeteSpalte } from './altplanAnlage';
import type { AltplanAnlage, DbZeile } from './altplanAnlage';

export interface AltplanGespeichert {
  planId: string;
  /** Verzichtbare Spalten, die die Datenbank nicht kannte und die deshalb fehlen. */
  ausgelassen: string[];
}

export const legeAltplanAn = async (anlage: AltplanAnlage): Promise<AltplanGespeichert> => {
  const angelegt: { tabelle: string; ids: string[] }[] = [];
  const ausgelassen: string[] = [];
  let plan: DbZeile = { ...anlage.plan };
  try {
    // Der Kopfsatz — notfalls ohne eine verzichtbare Spalte nach der anderen.
    for (;;) {
      const { error } = await supabase.from('sige_projekte').insert(plan);
      if (!error) break;
      const spalte = beanstandeteSpalte(error.message, plan);
      if (!spalte) throw new Error(`Der Plan konnte nicht angelegt werden: ${error.message}`);
      const { [spalte]: _weg, ...rest } = plan;
      plan = rest;
      ausgelassen.push(spalte);
    }
    angelegt.push({ tabelle: 'sige_projekte', ids: [anlage.plan.id] });

    const kinder: [string, DbZeile[]][] = [
      ['sige_arbeitsschritte', anlage.arbeitsschritte],
      ['sige_bloecke', anlage.bloecke],
    ];
    for (const [tabelle, zeilen] of kinder) {
      if (!zeilen.length) continue;
      const { error } = await supabase.from(tabelle).insert(zeilen);
      if (error) throw new Error(`${tabelle} konnte nicht geschrieben werden: ${error.message}`);
      angelegt.push({ tabelle, ids: zeilen.map(z => String(z.id)) });
    }
    return { planId: anlage.plan.id, ausgelassen };
  } catch (e) {
    for (const { tabelle, ids } of [...angelegt].reverse()) {
      const { error } = await supabase.from(tabelle).delete().in('id', ids);
      if (error) console.warn(`Rücknahme in ${tabelle} fehlgeschlagen:`, error.message);
    }
    throw e;
  }
};
